import { Box, Collapse, RadioGroup } from "@mui/material";
import Grid from "@mui/material/Grid2";
import React from "react";
import { DotLegalDialog, DotLegalHeader, DotLegalRadioButton, DotLegalSelect, DotLegalTextField } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../../../../localization/useTranslation";
import DotLegalFileUploadWrapper from "../../../../common/components/dotLegalFileUpload/DotLegalFileUploadWrapper";
import { isNullOrWhitespace } from "../../../../common/stringOperations";
import { useUserContext } from "../../../../auth/userContextProvider/UserContextProvider";
import { ContractDocumentRow } from "../contractDocuments/ContractDocuments.types";
import { useAddEditContractDialogDocumentHook } from "./AddEditContractDocumentDialog.hooks";
import DocumentVersionTable from "../../../../documents/documentVersionTable/DocumentVersionTable";
import { EntityType } from "../../../../common/Common.types";
import TagsMultiSelect from "../../../../common/components/tagsMultiSelect/TagsMultiSelect";
import { TagRelationType } from "../../../../common/components/tagsMultiSelect/TagsMultiSelect.types.ts";
import DotLegalSection from "@common/components/dotLegalSection/DotLegalSection.tsx";
import GroupEntitySelector from "@common/components/GroupEntitySelector";
import MentionInput from "@common/components/mentionInput/MentionInput";
import UploadProgressBar from "@common/components/uploadProgress/UploadProgressBar";

export interface IAddEditContractDocumentDialogProps {
    onDialogClose: () => void;
    selectedDocument?: ContractDocumentRow;
    isEdit: boolean;
    contractId: string;
    contractName?: string;
}

function AddEditContractDocumentDialog(props: IAddEditContractDocumentDialogProps) {
    const { translateString } = useTranslation();
    const { permissions } = useUserContext();
    const {
        document,
        documentTypes,
        inProgress,
        validation,
        validateDocumentForm,
        userOptions,
        userOptionsIsLoading,
        documentationOption,
        documentStatus,
        documentTypesIsLoading,
        documentDataUpdater,
        documentVersionIsUploading,
        setDocumentVersionIsUploading,
        selectedTags,
        showGroupEntityAccessPicker,
        selectableGroupEntities,
        groupEntityAccessPickerIsLoading,
        appliesToAllGroupEntities,
        isUploading,
        uploadPercentage,
    } = useAddEditContractDialogDocumentHook(props);

    validation?.check(document);

    return (
        <DotLegalDialog
            header={props.isEdit ? translateString("editDocument") : translateString("uploadDocument")}
            size="md"
            isLoading={false}
            buttonOkText={props.isEdit ? translateString("save") : translateString("upload")}
            open
            onDialogClose={() => props.onDialogClose()}
            onOkClick={() => validateDocumentForm()}
            inProgress={inProgress}
            disableBackdropOnCloseIfValueChange={document}
            okButtonDisabled={documentVersionIsUploading}
        >
            <Grid container columnSpacing={2}>
                <Grid size={{ md: 6 }}>
                    <DotLegalTextField
                        label={translateString("name")}
                        value={document?.name!}
                        debounce={false}
                        onChange={documentDataUpdater.onNameChange}
                        errorText={validation?.getErrorsForDisplay("name")}
                    />
                </Grid>

                <Grid size={{ md: 6 }}>
                    <DotLegalSelect
                        selectedItem={document?.documentTypeId ? document?.documentTypeId.toString() : undefined}
                        options={documentTypes}
                        placeholder={translateString("type")}
                        label={translateString("type")}
                        disableClearable
                        onChange={documentDataUpdater.onTypeChange}
                        errorText={validation?.getErrorsForDisplay("documentTypeId")}
                        noOptionsLabel={translateString("noOptions")}
                        isLoading={documentTypesIsLoading}
                    />
                </Grid>

                <Grid size={{ md: 6 }}>
                    <DotLegalSelect
                        selectedItem={document?.status ? document?.status.toString() : undefined}
                        options={documentStatus}
                        placeholder={translateString("documentStatus")}
                        label={translateString("documentStatus")}
                        disableClearable
                        onChange={documentDataUpdater.onStatusChange}
                        errorText={validation?.getErrorsForDisplay("status")}
                        noOptionsLabel={translateString("noOptions")}
                    />
                </Grid>

                <Grid size={{ md: 6 }}>
                    <DotLegalSelect
                        selectedItem={document?.ownerId}
                        options={userOptions}
                        placeholder={translateString("owner")}
                        label={translateString("owner")}
                        onChange={documentDataUpdater.onOwnerChange}
                        isLoading={userOptionsIsLoading}
                        noOptionsLabel={translateString("noOptions")}
                    />
                </Grid>

                {permissions.canAccessTags && (
                    <Grid size={{ md: 12 }}>
                        <TagsMultiSelect
                            selectedTags={selectedTags}
                            onChange={documentDataUpdater.onTagsChange}
                            isLoading={false}
                            entityType={TagRelationType.ContractDocument}
                            disabled={!permissions.contractManagementPermissions.permissions.edit}
                            variant="outlined"
                        />
                    </Grid>
                )}
            </Grid>
            {/* Rich text: archiving a document into a standalone contract writes a mention of that contract here.
                Typing new mentions stays off, since a comment saves no relations for them. */}
            <MentionInput
                label={translateString("comments")}
                value={document?.comment ?? ""}
                multiline
                rows={3}
                onChange={documentDataUpdater.onCommentChange}
                disableMentions
            />
            {props.isEdit ? (
                <Box marginTop={2} marginBottom={2}>
                    <DocumentVersionTable
                        documentId={props.selectedDocument!.documentId}
                        documentsUploading={documentVersionIsUploading}
                        setDocumentsUploading={setDocumentVersionIsUploading}
                        entityType={EntityType.Contract}
                        entityId={props.contractId}
                        entityName={props.contractName}
                    />
                </Box>
            ) : (
                <Box marginTop={2} marginBottom={2}>
                    <DotLegalHeader marginBottom={1} headerStyle={"small"}>
                        {translateString("uploadDocumentation")}
                    </DotLegalHeader>
                    <DotLegalSection>
                        <RadioGroup
                            row
                            value={documentationOption}
                            onChange={(e) => {
                                documentDataUpdater.onDocumentationTypeChange(Number((e.target as HTMLInputElement).value));
                            }}
                        >
                            <DotLegalRadioButton value={1} labelPlacement="end" label={translateString("uploadDocument")} />
                            <DotLegalRadioButton value={0} labelPlacement="end" label={translateString("insertLink")} />
                        </RadioGroup>

                        <>
                            <Collapse in={documentationOption === 1}>
                                {
                                    <DotLegalFileUploadWrapper
                                        errorText={validation?.getErrorsForDisplay("fileName")}
                                        files={isNullOrWhitespace(document?.fileName!) ? [] : [document?.fileName!]}
                                        onFilesChanged={documentDataUpdater.onFileUploadChange}
                                        isLoading={false}
                                        pasteEnabled={documentationOption === 1}
                                    />
                                }
                            </Collapse>
                            <Collapse in={documentationOption === 0}>
                                <Box sx={{ "& > *": { marginTop: 0 } }}>
                                    <DotLegalTextField
                                        label={translateString("insertLink")}
                                        value={document?.link ?? ""}
                                        debounce={false}
                                        errorText={validation?.getErrorsForDisplay("link")}
                                        onChange={documentDataUpdater.onLinkChange}
                                    ></DotLegalTextField>
                                </Box>
                            </Collapse>
                        </>
                    </DotLegalSection>
                </Box>
            )}
            {showGroupEntityAccessPicker && (
                <Box marginTop={2}>
                    <GroupEntitySelector
                        switchLabel={translateString("contractDocumentVisibleToEveryoneWithContractAccess")}
                        explanation={translateString("contractDocumentGroupEntityRestriction")}
                        selectedUserGroupCompanies={document.groupEntityAccessIds.map((id) => ({ userId: null, groupCompanyId: id }))}
                        onGroupCompaniesChange={documentDataUpdater.onGroupEntityAccessChange}
                        legalEntityItems={selectableGroupEntities}
                        isLoading={groupEntityAccessPickerIsLoading}
                        appliesToAllGroupEntities={appliesToAllGroupEntities}
                        onAppliesToAllGroupEntitiesChange={documentDataUpdater.onAppliesToAllGroupEntitiesChange}
                        showError={!!validation?.getErrorsForDisplay("groupEntityAccessIds")}
                        errorText={validation?.getErrorsForDisplay("groupEntityAccessIds")}
                        visibleToNonGdpo
                        disableSelectAll
                    />
                </Box>
            )}
            <UploadProgressBar isUploading={isUploading} uploadPercentage={uploadPercentage} />
        </DotLegalDialog>
    );
}

export default AddEditContractDocumentDialog;
